import React, { useMemo, useState } from "react";
import { Column, FilterCondition } from "../types/analysisList";
import { useForm } from "@mantine/form";
import { DateRange, resolveDateParams } from "../utils/datePreset";
import { DatePickerInput } from "@mantine/dates";
import { CalendarBlankIcon } from "@phosphor-icons/react";
import { Box, Button, Group, NumberInput, Select, Text, TextInput } from "@mantine/core";
import { DatePresetFilter } from "./DatePresetFilter";

interface Props {
  columns: Column[]
  onSubmit: (values: FormData) => Promise<void> | void
  disabled?: boolean
}

export interface FormData {
  date: string
  startDate: string | null
  endDate: string | null
  column: string | null
  condition: FilterCondition | null
  value: string
  value2: string
}

const conditionOptions: { value: FilterCondition; label: string }[] = [
  { value: 'like', label: '含む' },
  { value: 'or_more', label: '以上' },
  { value: 'or_less', label: '以下' },
  { value: 'between', label: 'の間' },
  { value: 'is_not_null', label: '存在する' },
  { value: 'is_null', label: '存在しない' },
]

const NO_VALUE_CONDITIONS: FilterCondition[] = ['is_null', 'is_not_null']
const TWO_VALUE_CONDITIONS: FilterCondition[] = ['between']

const trimWhitespace = (s: string) =>
  s.replace(/^[\s　]+|[\s　]+$/g, '')

export const AnalysisListSearchForm = ({ columns, onSubmit, disabled = false }: Props) => {
  const [loading, setLoading] = useState(false)

  const form = useForm<{
    date: string
    dateRange: DateRange
    column: string | null
    condition: FilterCondition | null
    value: string
    value2: string
  }>({
    initialValues: {
      date: '',
      dateRange: [null, null],
      column: null,
      condition: null,
      value: '',
      value2: '',
    },
  })

  const canSubmit = useMemo(() => {
    const { date, dateRange, column, condition, value, value2 } = form.values

    // custom で片方未入力のまま送ると期間フィルタが効かず全期間になるため送信を止める
    if (date === 'custom' && (!dateRange[0] || !dateRange[1])) return false
    if (column && condition) {
      if (NO_VALUE_CONDITIONS.includes(condition)) return true
      if (!value) return false
      if (TWO_VALUE_CONDITIONS.includes(condition) && !value2) return false
    }
    return true
  }, [form.values])

  const handleColumnChange = (v: string | null) => {
    form.setFieldValue('column', v)
    form.setFieldValue('condition', null)
    form.setFieldValue('value', '')
    form.setFieldValue('value2', '')
  }

  const selectedColumn = columns.find(c => c.field === form.values.column)
  const columnType = selectedColumn?.cellDataType ?? 'string'

  const renderValueInput = (field: 'value' | 'value2', placeholder: string) => {
    if (columnType === 'date') {
      return (
        <DatePickerInput
          placeholder={placeholder}
          valueFormat="YYYY.MM.DD"
          value={form.values[field] || null}
          onChange={(d) => form.setFieldValue(field, d ?? '')}
          error={form.errors[field]}
          rightSection={
            <CalendarBlankIcon
              size={16}
              weight="fill"
              color={form.errors[field] ? "#FA5252" : "#00000099"}
            />
          }
          rightSectionPointerEvents="none"
          locale="ja"
          w={160}
        />
      )
    }
    if (columnType === 'integer') {
      return (
        <NumberInput
          placeholder={placeholder}
          value={form.values[field] === "" ? "" : Number(form.values[field])}
          onChange={(v) =>
            form.setFieldValue(field, v === "" || v === null ? "" : String(v))
          }
          error={form.errors[field]}
          hideControls
          w={160}
          max={Number.MAX_SAFE_INTEGER}
          min={Number.MIN_SAFE_INTEGER}
          clampBehavior="strict"
        />
      )
    }

    return (
      <TextInput
        placeholder={placeholder}
        {...form.getInputProps(field)}
        w={160}
      />
    )
  }

  return (
    <Box bg="#00000008" p={16} mt={40} bdrs={8}>
      <form onSubmit={form.onSubmit(async ({ date, dateRange, ...rest }) => {
        setLoading(true)

        try {
          await onSubmit({
            ...rest,
            value: trimWhitespace(rest.value),
            value2: trimWhitespace(rest.value2),
            ...resolveDateParams(date, dateRange),
          })
        } finally {
          setLoading(false)
        }
      })}>
        <DatePresetFilter
          value={form.values.date}
          dateRange={form.values.dateRange}
          onChangeValue={(v) => form.setFieldValue('date', v)}
          onChangeDateRange={(r) => form.setFieldValue('dateRange', r)}
          withNoneOption
        />

        <Group gap={24} mt={16} align="flex-start" style={{ alignItems: "center" }}>
          <Text fz={13} fw={600} c="#000000CC" style={{ width: "100px", textAlign: "start" }}>絞り込み条件</Text>
          <Group gap={8} align="flex-start" wrap="nowrap">
            <Select
              placeholder="カラムを選択"
              data={columns.map(c => ({ value: c.field, label: c.headerName }))}
              searchable
              clearable
              nothingFoundMessage="該当するカラムがありません"
              comboboxProps={{ withinPortal: false }}
              value={form.values.column}
              onChange={handleColumnChange}
              w={300}
            />
            <Select
              placeholder="条件"
              data={conditionOptions}
              clearable
              comboboxProps={{ withinPortal: false }}
              value={form.values.condition}
              onChange={(v) => form.setFieldValue('condition', v as FilterCondition | null)}
              disabled={!form.values.column}
              w={140}
            />
            {form.values.condition && !NO_VALUE_CONDITIONS.includes(form.values.condition) && (
              renderValueInput('value', form.values.condition === 'between' ? 'から' : '入力')
            )}
            {form.values.condition && TWO_VALUE_CONDITIONS.includes(form.values.condition) && (
              renderValueInput('value2', 'まで')
            )}
          </Group>
        </Group>

        <Group justify="end" mt={16} gap={8}>
          <Button
            variant="subtle"
            color="gray"
            radius="xl"
            type="button"
            fz={13}
            fw={600}
            onClick={() => {
              form.setFieldValue('column', null)
              form.setFieldValue('condition', null)
              form.setFieldValue('value', '')
              form.setFieldValue('value2', '')
            }}
            disabled={loading || disabled}
          >
            絞込条件をリセット
          </Button>
          <Button
            variant="filled"
            radius="xl"
            type="submit"
            fz={13}
            fw={600}
            w={120}
            disabled={loading || disabled || !canSubmit}
          >
            {loading ? '処理中' : '表示'}
          </Button>
        </Group>
      </form>
    </Box>
  )
}
